import {
	type FileToUpload,
	NotificationService,
	type StepperRef,
	uploadFiles,
	useAppLoader,
	useIntl,
	type UserRequirementDocumentCategory,
	type UserRequirementStateStatus,
	type UserSourceOfIncomeRequirementConfig
} from '@capsicum/common';
import { type Requirement } from '@capsicum/socios-core';
import { useAtom, useSetAtom } from 'jotai';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SOI_MAX_FILE_SIZE } from '../../constants';
import { sourceOfIncomeAtom, uniqueRequirementAtom } from '../../data-access';
import { type SourceOfIncomeCategoryState } from '../../data-access/atoms/source-of-income.atom';
import {
	useGetRequirementCategoriesByIdentifierQuery,
	useSubmitRequirementStateByIdentifierQuery
} from '../../data-access/queries/useRequirementsQueries';
import { type CategoryStepsRefProps, type SOISubmissionList } from '../../models/source-of-income';

type UseSourceOfIncomeProps = {
	isUniqueRequirement?: boolean;
	requirement: Requirement<UserSourceOfIncomeRequirementConfig>;
	status?: UserRequirementStateStatus;
};

type FilesForSubmission = {
	[categoryId: string]: {
		[requirementId: string]: Array<string>;
	};
};

type UploadedFile = {
	category: string;
	image: FileToUpload;
	requestId: string;
	requirement: string;
};

const extractImagesForUpload = (state: SourceOfIncomeCategoryState) => {
	const imagesToUpload: Array<{
		category: string;
		image: FileToUpload;
		requirement: string;
	}> = [];

	Object.entries(state).forEach(([categoryId, categoryData]) => {
		Object.entries(categoryData.requirements || {}).forEach(([requirementId, images]) => {
			images.forEach(image => {
				imagesToUpload.push({ category: categoryId, image, requirement: requirementId });
			});
		});
	});

	return imagesToUpload;
};

const parseFilesForSubmission = (uploadedFiles: Array<UploadedFile>): FilesForSubmission => {
	return uploadedFiles.reduce<FilesForSubmission>((acc, file) => {
		const { category, requestId, requirement } = file;

		if (!acc[category]) {
			acc[category] = {};
		}

		if (!acc[category][requirement]) {
			acc[category][requirement] = [];
		}

		acc[category][requirement].push(requestId);

		return acc;
	}, {});
};

const parseSubmissionList = (state: SourceOfIncomeCategoryState): SOISubmissionList => {
	return Object.entries(state).reduce<SOISubmissionList>((acc, [categoryId, categoryState]) => {
		const requirementImages = Object.values(categoryState.requirements || {}).flatMap(images => images);

		acc[categoryId] = {
			requirementImages,
			title: categoryState.title
		};

		return acc;
	}, {});
};

const filterAndUpdateCategoriesByRequestedDocuments = (
	soiRequestedDocuments: NonNullable<UserSourceOfIncomeRequirementConfig['soiRequestedDocuments']>,
	fullCategories: Array<UserRequirementDocumentCategory>
) => {
	return (
		fullCategories
			// eslint-disable-next-line no-prototype-builtins
			.filter(category => soiRequestedDocuments.hasOwnProperty(category.id))
			.map(category => ({
				...category,
				types: category.types
					?.filter(type => soiRequestedDocuments[category.id]?.includes(type.id))
					.map(type => ({
						...type,
						grouping: 1
					}))
			}))
	);
};

const validateCategory = ({
	categoryId,
	categorySource,
	groupBy,
	requirements
}: {
	categoryId: string;
	categorySource?: Array<UserRequirementDocumentCategory>;
	groupBy: 'grouping' | 'id';
	requirements?: Record<string, Array<FileToUpload>>;
}) => {
	if (!categorySource || !categorySource.length || !requirements) return false;
	const category = categorySource.find(category => category.id === categoryId);

	if (!category) return false;

	const groupValidation: Record<number | string, boolean> = {};

	for (const type of category.types) {
		groupValidation[type[groupBy]] = false;
	}

	for (const { grouping, id, numberOfDocuments } of category.types) {
		const files = requirements[id] || [];

		if (files.some(file => file.size > SOI_MAX_FILE_SIZE)) return false;
		if (files.length > numberOfDocuments) return false;

		if (files.length > 0) {
			groupValidation[groupBy === 'grouping' ? grouping : id] = true;
		}
	}

	return Object.values(groupValidation).every(isValid => isValid);
};

export const MAX_NUMBER_OF_DOCUMENTS = 10;

export const useSourceOfIncome = ({ isUniqueRequirement, requirement, status }: UseSourceOfIncomeProps) => {
	const { $t, getLocalizedField } = useIntl();
	const appLoader = useAppLoader();
	const {
		data: categoryList,
		isLoading: areCategoriesLoading,
		isSuccess: categoriesLoaded
	} = useGetRequirementCategoriesByIdentifierQuery({
		requirementIdentifier: requirement.identifier
	});

	const {
		isLoading: isUpdatingRequirement,
		isSuccess: isSuccessRequirement,
		mutate: mutateRequirement
	} = useSubmitRequirementStateByIdentifierQuery();

	const stepperRef = useRef<StepperRef>(null);
	const [stepIndex, setStepIndex] = useState(0);
	const [state, dispatch] = useAtom(sourceOfIncomeAtom);
	const setShowRequirementPage = useSetAtom(uniqueRequirementAtom);

	const isUnderReview = status === 'under_review';
	const areDocumentsRequested = Boolean(requirement.config.soiRequestedDocuments);
	const selectedCategories = useMemo(() => Object.keys(state), [state]);
	const parsedImages = useMemo(() => extractImagesForUpload(state), [state]);
	const translatedCategories = useMemo(() => {
		return categoryList?.map(category => ({
			...category,
			description: getLocalizedField('description', category),
			name: getLocalizedField('name', category)
		}));
	}, [categoryList]);

	const requestedDocuments = useMemo(
		() =>
			filterAndUpdateCategoriesByRequestedDocuments(
				requirement.config.soiRequestedDocuments || {},
				translatedCategories || []
			),
		[requirement.config.soiRequestedDocuments, translatedCategories]
	);

	const categoryOptions = categoryList?.map(category => {
		const name = getLocalizedField('name', category);

		return {
			name,
			value: category.id
		};
	});

	const categoryStepsRef = useRef<Record<string, CategoryStepsRefProps>>({});

	useEffect(() => {
		return () => dispatch({ type: 'RESET' });
	}, []);

	useEffect(() => {
		if (categoriesLoaded && requestedDocuments.length) {
			requestedDocuments.forEach(({ id, name: title }) => {
				dispatch({ category: id, title, type: 'ADD_CATEGORY' });
			});
		}
	}, [categoriesLoaded]);

	const onSuccess = () => {
		if (isUniqueRequirement) setShowRequirementPage(false);
	};

	const handleCheckbox = useCallback(
		(isChecked: boolean, value: string) => {
			if (isChecked) {
				const title = categoryList?.find(category => category.id === value)?.name as string;

				dispatch({ category: value, title, type: 'ADD_CATEGORY' });
			} else {
				dispatch({ category: value, type: 'REMOVE_CATEGORY' });
			}
		},
		[categoryList]
	);

	const submit = async () => {
		const result = await uploadFiles(parsedImages);

		mutateRequirement(
			{
				requirementData: { documentsContent: parseFilesForSubmission(result) },
				requirementIdentifier: requirement.identifier
			},
			{ onSuccess }
		);
	};

	const handleSubmit = () => appLoader(submit());

	const navigateToCategory = useCallback(
		(categoryId: string) => {
			const index = selectedCategories.indexOf(categoryId);

			stepperRef.current?.moveToStepIndex(index + (areDocumentsRequested ? 0 : 1));
		},
		[selectedCategories]
	);

	const deleteAllCategoryFiles = useCallback(
		(categoryId: string) => {
			dispatch({ category: categoryId, type: 'REMOVE_CATEGORY_FILES' });
		},
		[dispatch]
	);

	const validateCategoryGroups = (categoryId: string) =>
		validateCategory({
			categoryId,
			categorySource: categoryList,
			groupBy: 'grouping',
			requirements: state[categoryId]?.requirements
		});

	const validateUniqueGroup = (categoryId: string) =>
		validateCategory({
			categoryId,
			categorySource: requestedDocuments,
			groupBy: 'id',
			requirements: state[categoryId]?.requirements
		});

	const isFirstButtonDisabled = useMemo(() => {
		if (areDocumentsRequested) {
			if (stepIndex === selectedCategories.length) return false;

			const categoryId = selectedCategories[stepIndex] as string;

			return !validateUniqueGroup(categoryId);
		}

		if (stepIndex === 0) return selectedCategories.length === 0;
		if (stepIndex === selectedCategories.length + 1) {
			const areSomeCategoryDocsRemoved = selectedCategories.some(
				categoryId => !validateCategoryGroups(categoryId)
			);

			return areSomeCategoryDocsRemoved || parsedImages.length > MAX_NUMBER_OF_DOCUMENTS;
		}

		const categoryId = selectedCategories[stepIndex - 1] as string;

		return !validateCategoryGroups(categoryId);
	}, [stepIndex, selectedCategories, parsedImages]);

	const submissionList = useMemo(() => parseSubmissionList(state), [state]);

	const storeCategoryRef = useCallback(
		(categoryId: string, ref: CategoryStepsRefProps | null) => {
			if (ref) {
				categoryStepsRef.current[categoryId] = ref;
			} else {
				delete categoryStepsRef.current[categoryId];
			}
		},
		[categoryStepsRef]
	);

	const deleteAllDocuments = useCallback(
		(categoryId: string, component: ReactElement) => {
			deleteAllCategoryFiles(categoryId);
			const categoryStep = categoryStepsRef.current[categoryId];

			if (categoryStep) {
				categoryStep?.resetCategoryFiles();
			}

			NotificationService.addNotification({
				component
			});
		},
		[deleteAllCategoryFiles]
	);

	const stepCounter = useMemo(() => {
		const currentStep = stepIndex + (areDocumentsRequested ? 1 : 0);

		return $t('sourceOfIncome.category.step', { step: `0${currentStep}/0${selectedCategories.length}` });
	}, [stepIndex, selectedCategories]);

	const isSubmitting = isUpdatingRequirement || isSuccessRequirement;
	const availableCategories = (areDocumentsRequested ? requestedDocuments : translatedCategories) || [];
	const showLoader = areCategoriesLoading || !availableCategories || !categoryOptions || isUnderReview;

	return {
		areDocumentsRequested,
		availableCategories,
		categoryOptions: categoryOptions || [],
		deleteAllCategoryFiles,
		deleteAllDocuments,
		handleCheckbox,
		handleSubmit,
		isFirstButtonDisabled,
		isMaxFilesAmountExceeded: parsedImages.length > MAX_NUMBER_OF_DOCUMENTS,
		isSubmitting,
		isUnderReview,
		maxFilesAmount: MAX_NUMBER_OF_DOCUMENTS,
		navigateToCategory,
		selectedCategories,
		setStepIndex,
		showLoader,
		stepCounter,
		stepperRef,
		storeCategoryRef,
		submissionList
	};
};
